import { DatePicker } from "src/components/ui/DatePicker";
import { useRwoTaskList } from "./hooks/useRwoTaskList";
import TaskTableRwo from "./TaskTableRwo";
import { ClipboardCheck, Trash } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { TaskListRwo as TaskListRwoType } from "./types/dailyTaskRwo";

const TaskListRwo = ({
  select,
}: {
  select: (task: TaskListRwoType) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const date = searchParams.get("date") ?? "";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10));

  const filters = {
    date,
    page,
  };

  const { data: response, isLoading } = useRwoTaskList(filters);
  const tasks = response?.rwo_daily_trackers ?? [];

  const total = response?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (response?.pagesize ?? 10)));

  const handleNext = () => {
    if (page < totalPages) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", String(page + 1));
        return params;
      });
    }
  };

  const handlePrev = () => {
    if (page > 1) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", String(page - 1));
        return params;
      });
    }
  };

  const selectPage = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", String(pageNumber));
        return params;
      });
    }
  };

  const handleDateChange = (newDate: Date | undefined) => {
    const formatted = newDate ? moment(newDate).format("YYYY-MM-DD") : "";

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (formatted) next.set("date", formatted);
        else next.delete("date");
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  return (
    <div className="space-y-6 my-3">
      <section className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br p-5 sm:p-7 from-blue-950/30 via-background to-background">
        <div className="pointer-events-none absolute -right-12 -top-16 size-48 rounded-full bg-blue-500/[0.06] blur-3xl" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#243F71] text-white shadow-md shadow-blue-950/10 sm:size-14">
              <ClipboardCheck className="size-6 sm:size-7" />
            </div>
            <div className="min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#42679e]">
                  Field Operations
                </span>
                <span className="text-muted-foreground/50">/</span>
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Reports
                </span>
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                RWO Daily Trackers
              </h1>
              <p className="max-w-xl text-sm leading-6 text-muted-foreground">
                Monitor submissions, review daily field activities, and track
                RWO performance.
              </p>
            </div>
          </div>

          <div className="flex gap-3 items-center justify-center backdrop-blur-lg bg-gray-300 p-1 rounded-lg">
            <DatePicker
              value={date ? new Date(date) : undefined}
              onChange={handleDateChange}
            />
            <button
              type="button"
              onClick={clearFilters}
              className="p-2 h-10 text-sm rounded-full border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 transition"
            >
              <Trash />
            </button>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="p-6 text-center bg-gray-900 border border-gray-700 rounded-2xl shadow animate-pulse">
          <div className="text-gray-500 dark:text-gray-400 flex items-center justify-center gap-4 h-[10dvh]">
            <span>Loading...</span>{" "}
            <span className="loading loading-ring loading-lg"></span>
          </div>
        </div>
      ) : (
        <section className="submissions-section">
          <TaskTableRwo
            tasks={tasks}
            page={page}
            pagesize={response?.pagesize ?? 10}
            total={total}
            nextPage={handleNext}
            prevPage={handlePrev}
            selectPage={selectPage}
            selectTask={select}
          />
        </section>
      )}
    </div>
  );
};

export default TaskListRwo;
